/**
 * Editorial Impact Stories for the public homepage.
 *
 * These are documented public projects run by public bodies elsewhere — they are
 * NOT Xamseen records. Every entry links back to the authoritative source that
 * published it, and carries only what that source states. Nothing here is a
 * statistic, quote or outcome invented by Xamseen.
 *
 * Stories are curated by admins/moderators in the Admin → Stories editor and
 * stored in `impact_stories`. The constants below are the launch set kept as a
 * render fallback if the table cannot be read.
 */

import { supabase } from "@/integrations/supabase/client";

export type ImpactStory = {
  place: string;
  country: string;
  title: string;
  body: string;
  responsible: string;
  source: { publisher: string; url: string };
};

export type ImpactStoryRow = {
  id: string;
  place: string;
  country: string;
  title: string;
  body: string;
  responsible: string;
  source_publisher: string;
  source_url: string;
  position: number;
  published: boolean;
  updated_at: string;
};

export const IMPACT_STORIES: ImpactStory[] = [
  {
    place: "Helsinki",
    country: "Finland",
    title: "Crown Bridges tram link",
    body:
      "A tram connection and new bridges linking the Helsinki city centre to Laajasalo across Korkeasaari, published as an ongoing city construction project with its own public project site.",
    responsible: "City of Helsinki",
    source: { publisher: "kruunusillat.fi — City of Helsinki", url: "https://www.kruunusillat.fi/en/" },
  },
  {
    place: "New York City",
    country: "United States",
    title: "Open Streets",
    body:
      "A programme that turns selected streets into space open to all, run with local partners who apply to become stewards of their own block.",
    responsible: "NYC Department of Transportation",
    source: {
      publisher: "NYC DOT",
      url: "https://www.nyc.gov/html/dot/html/pedestrians/openstreets.shtml",
    },
  },
  {
    place: "Lagos",
    country: "Nigeria",
    title: "Lagos rail and bus network",
    body:
      "Lagos State's public transport programme, including the city rail lines and the regulated bus scheme, published by the state transport authority that delivers it.",
    responsible: "Lagos Metropolitan Area Transport Authority (LAMATA)",
    source: { publisher: "LAMATA, Lagos State", url: "https://lamata.ng/" },
  },
];

export function rowToStory(r: ImpactStoryRow): ImpactStory {
  return {
    place: r.place,
    country: r.country,
    title: r.title,
    body: r.body,
    responsible: r.responsible,
    source: { publisher: r.source_publisher, url: r.source_url },
  };
}

/** Published stories for the public homepage, in editorial order. */
export async function fetchPublishedImpactStories(): Promise<ImpactStory[]> {
  const { data, error } = await supabase
    .from("impact_stories")
    .select("*")
    .eq("published", true)
    .order("position", { ascending: true })
    .limit(12);
  if (error || !data?.length) return IMPACT_STORIES;
  return (data as ImpactStoryRow[]).map(rowToStory);
}

/** Every story, drafts included — only readable by editors. */
export async function fetchAllImpactStories(): Promise<ImpactStoryRow[]> {
  const { data, error } = await supabase
    .from("impact_stories")
    .select("*")
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ImpactStoryRow[];
}

export type ImpactStoryDraft = {
  place: string;
  country: string;
  title: string;
  body: string;
  responsible: string;
  source_publisher: string;
  source_url: string;
  position: number;
  published: boolean;
};

/** An authoritative source must be an absolute http(s) link. */
export function validateStory(d: ImpactStoryDraft): string | null {
  if (!d.title.trim()) return "Give the story a title.";
  if (!d.place.trim() || !d.country.trim()) return "Add the place and country.";
  if (!d.body.trim()) return "Add the description, using only what the source states.";
  if (!d.responsible.trim()) return "Name the body responsible for the project.";
  if (!d.source_publisher.trim()) return "Credit the publisher of the source.";
  try {
    const u = new URL(d.source_url.trim());
    if (u.protocol !== "https:" && u.protocol !== "http:") return "The source link must be an http(s) URL.";
  } catch {
    return "Add a full source URL, including https://";
  }
  return null;
}

export async function createImpactStory(d: ImpactStoryDraft, userId: string) {
  const { error } = await supabase.from("impact_stories").insert({ ...d, updated_by: userId });
  if (error) throw error;
}

export async function updateImpactStory(id: string, d: Partial<ImpactStoryDraft>, userId: string) {
  const { error } = await supabase.from("impact_stories").update({ ...d, updated_by: userId }).eq("id", id);
  if (error) throw error;
}

export async function deleteImpactStory(id: string) {
  const { error } = await supabase.from("impact_stories").delete().eq("id", id);
  if (error) throw error;
}
